import { useCallback, useEffect, useRef, useState } from "react";

import { acaoConfirmarCancelamento, acaoDesmarcarPronto, acaoMarcarProntos } from "../lib/acoes";
import { chaveDaComanda } from "../lib/envios";

/** Produtos selecionados na fila da cozinha, por comanda.
 *
 *  Regra do dono (22/09/2026): tocar no produto NÃO marca pronto, só seleciona.
 *  O botão da comanda vira "Marcar N produtos como pronto" e é ele que grava.
 *  Evita pronto por toque errado e deixa marcar vários de uma vez.
 *
 *  Cada comanda guarda a própria seleção — o bartender pode ir escolhendo a
 *  caipirinha em três comandas e confirmar uma por uma.
 *
 *  Seleção parada se solta sozinha: 10 s sem tocar em nada daquela comanda e
 *  ela volta a zero (pedido do dono). Seleção esquecida faz o próximo toque no
 *  botão marcar pronto o que ninguém lembrava de ter escolhido.
 *
 *  `pedidos` é a fila já separada por envio (lib/envios.js): a seleção é da
 *  comanda NA TELA (`chaveDaComanda`), a gravação é no pedido (`id`).
 *
 *  Nada aqui fala com o servidor: cada marcação vira uma ação de
 *  `lib/acoes.js` e passa pelo `agendar` do Desfazer (hooks/useDesfazer.js),
 *  que mostra na hora e grava quando o tempo de desfazer acaba.
 */

const SOLTA_EM_MS = 10000;
export default function useSelecaoItens({ pedidos, agendar }) {
  const [marcados, setMarcados] = useState({}); // chave da comanda -> [_idx]
  const relogios = useRef({}); // chave da comanda -> timeout da soltura

  const pararRelogio = (pid) => {
    clearTimeout(relogios.current[pid]);
    delete relogios.current[pid];
  };
  useEffect(() => () => Object.values(relogios.current).forEach(clearTimeout), []);

  // Seleção que perdeu o sentido sai sozinha: item que ficou pronto por outra
  // tela, item que sumiu da comanda, comanda que saiu da produção.
  useEffect(() => {
    setMarcados((atual) => {
      let mudou = false;
      const novo = {};
      Object.entries(atual).forEach(([chave, idxs]) => {
        const p = pedidos.find((x) => chaveDaComanda(x) === chave);
        if (!p || p.status !== "producao") { mudou = true; return; }
        const validos = idxs.filter((i) => p.items.some((it) => it._idx === i && !it.pronto_at && !it.deleted));
        if (validos.length !== idxs.length) mudou = true;
        if (validos.length) novo[chave] = validos;
      });
      return mudou ? novo : atual;
    });
  }, [pedidos]);

  const selecionadosDe = useCallback((chave) => marcados[chave] || [], [marcados]);

  const alternar = (pedido, item) => {
    if (item.pronto_at) return;
    const chave = chaveDaComanda(pedido);
    // Cada toque na comanda recomeça os 10 s dela.
    pararRelogio(chave);
    relogios.current[chave] = setTimeout(() => limpar(chave), SOLTA_EM_MS);
    setMarcados((prev) => {
      const atual = prev[chave] || [];
      const proximo = atual.includes(item._idx) ? atual.filter((i) => i !== item._idx) : [...atual, item._idx];
      const novo = { ...prev };
      if (proximo.length) novo[chave] = proximo; else delete novo[chave];
      return novo;
    });
  };

  function limpar(chave) {
    pararRelogio(chave);
    setMarcados((prev) => {
      if (!prev[chave]) return prev;
      const novo = { ...prev };
      delete novo[chave];
      return novo;
    });
  }
  const limparTudo = () => {
    Object.keys(relogios.current).forEach(pararRelogio);
    setMarcados({});
  };

  /** Os itens vão um por vez pro servidor: se um falhar (comanda editada no
   *  meio), os outros não ficam num estado que ninguém viu. O último que fecha
   *  a comanda faz ela subir sozinha, no servidor. */
  const marcarItens = (pedido, itens) => {
    if (!itens.length) return undefined;
    limpar(chaveDaComanda(pedido));
    return agendar(acaoMarcarProntos(pedido, itens));
  };

  const marcarSelecionados = (pedido) => {
    const idxs = marcados[chaveDaComanda(pedido)] || [];
    return marcarItens(pedido, (pedido.items || []).filter((i) => idxs.includes(i._idx) && !i.pronto_at));
  };

  /** "Confirmar o cancelamento" no cartão que só tem produto cancelado na mesa:
   *  grava que a cozinha viu (vale pra todas as telas) e o cartão sai. */
  const confirmarCancelamento = (card) => agendar(acaoConfirmarCancelamento(card));

  /** "Marcar Pronto" na tela de um setor (dono, 25/09/2026): só o que o cartão
   *  mostra — os produtos desta praça (e os sem setor, que aparecem em toda
   *  tela). Subir o pedido marcava junto o que as outras praças ainda faziam.
   *  O último produto que fica pronto sobe a comanda sozinho, no servidor. */
  const marcarDoCartao = (card) => marcarItens(
    card, (card._itens || []).filter((i) => !i._cancelado && !i.deleted && !i.pronto_at),
  );

  /** "Marcar Pronto" num cartão de envio: o envio inteiro, não o pedido — os
   *  outros envios da mesa seguem na cozinha. */
  const marcarEnvio = (pedido) => marcarItens(
    pedido, (pedido.items || []).filter((i) => !i.deleted && !i.pronto_at),
  );

  /** Desfazer tem botão próprio no item pronto, pelo mesmo motivo: nada muda
   *  de estado por um toque na linha. */
  const desfazer = (pedido, item) => agendar(acaoDesmarcarPronto(pedido, item));

  return { selecionadosDe, alternar, limpar, limparTudo, marcarSelecionados, marcarDoCartao, marcarEnvio, desfazer, confirmarCancelamento };
}
